import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setUnauthorizedHandler } from "@/lib/api";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (err) {
      console.error("Logout request failed (clearing session anyway):", err);
    }
    setCurrentUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    const boot = async () => {
      try {
        const me = await api.me();
        setCurrentUser(me);
      } catch (err) {
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  const login = useCallback(async (email, password) => {
    const { user } = await api.login(email, password);
    setCurrentUser(user);
    return user;
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.me();
    setCurrentUser(me);
    return me;
  }, []);

  const changePassword = useCallback(async (current, next, confirm) => {
    const { user } = await api.changePassword(current, next, confirm);
    setCurrentUser(user);
    return user;
  }, []);

  const isAdmin = currentUser?.role === "administrator";
  const canWrite = !!currentUser && currentUser.role !== "viewer";
  const can = (area) => !!currentUser && !!currentUser[area];

  return (
    <UserContext.Provider
      value={{ currentUser, loading, login, logout, refreshUser, changePassword, isAdmin, canWrite, can }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
};
